import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { useFormikContext } from 'formik'
import styled from 'styled-components'
import { isEmpty, pick, pipe, reject, isNil } from 'ramda'
import { showUpdateGenerateTextModal } from '@/features/SmartDescription/generate-text'
import { toast } from '@/components/toast-notifications'
import Tooltip from '@/components/popper-tooltip'
import { showChangeToneModal, SmartButton } from '@/features/SmartDescription'
import useMediaQuery from '@/hooks/use-media-query'
import { breakpoints } from '@/styles'
import { showSmartOptionsModal } from './smart-text-modal'

const CustomBarWrapper = styled.div`
  display: flex;

  > :not(:last-child) {
    margin-right: 8px;
  }
`

const GENERATE_KEYS = [
  'building_id',
  'count_bedrooms',
  'count_bathrooms',
  'count_dens',
  'size',
  'max_occupancy',
  'furnished',
  'allow_cats',
  'allow_dogs',
  'allow_pets',
  'allow_smoking',
  'availability_date'
]

const ButtonBar = () => {
  const { t } = useTranslation()
  const { values, setFieldValue } = useFormikContext()
  const { listingId } = useParams()
  const generateValues = pipe(pick(GENERATE_KEYS), reject(isEmpty), reject(isNil))(values)

  const isMobile = useMediaQuery(`(max-width: ${breakpoints.phoneMax})`)

  const setDescriptionFormValue = value => {
    setFieldValue('description', value)
  }

  const handleEmptyDescription = () =>
    toast(
      t(
        'l.smart_description.change_tone.empty_description.error',
        'Please enter a description to continue and try again.'
      ),
      {
        type: 'warning',
        persistent: true
      }
    )

  const handleClickGenerateText = () => {
    showChangeToneModal({
      listingId: listingId,
      formValues: generateValues,
      initAction: 'create',
      heading: t('l.smart_description.generate_text.generate_text.title', 'Generate text'),
      setDescriptionFormValue: setDescriptionFormValue
    })
  }

  const handleClickChangeTone = () => {
    if (isEmpty(values?.description)) {
      handleEmptyDescription()
    } else {
      showChangeToneModal({
        description: values?.description,
        listingId: listingId,
        heading: t('l.smart_description.change_tone.change_tone.title', 'Change tone'),
        setDescriptionFormValue: setDescriptionFormValue
      })
    }
  }

  const handleClickCleanUp = () => {
    if (isEmpty(values?.description)) {
      handleEmptyDescription()
    } else {
      showUpdateGenerateTextModal({
        description: values?.description,
        listingId: listingId,
        tone: 'cleanup',
        heading: t('l.smart_description.generate_text.generate_text.title', 'Generate text'),
        setDescriptionFormValue: setDescriptionFormValue,
        eventId: 'application-description-ai-cleanup-click'
      })
    }
  }

  return isMobile ? (
    <CustomBarWrapper>
      <SmartButton
        id="application-description-ai-smarttext"
        onClick={() =>
          showSmartOptionsModal({
            listingId: listingId,
            generateValues: generateValues,
            values: values,
            setDescriptionFormValue: setDescriptionFormValue,
            handleEmptyDescription: handleEmptyDescription
          })
        }
      >
        {t('l.smart_description.generate_text.smart_text.title', 'Smart text')}
      </SmartButton>
    </CustomBarWrapper>
  ) : (
    <CustomBarWrapper>
      <Tooltip
        content={t(
          'l.smart_description.generate_text.tooltip.text',
          `Generate engaging listing descriptions instantly, powered by ChatGPT AI.`
        )}
        openOnClick={false}
      >
        <SmartButton id="application-description-ai-gentext" onClick={handleClickGenerateText}>
          {t('l.smart_description.generate_text.generate_text.title', 'Generate text')}
        </SmartButton>
      </Tooltip>
      <Tooltip
        content={t(
          'l.smart_description.clean_up.tooltip.text',
          `Enter a description and we will correct your grammar and clean the layout, powered by ChatGPT AI.`
        )}
        openOnClick={false}
      >
        <SmartButton id="application-description-ai-cleanup" onClick={handleClickCleanUp}>
          {t('l.smart_description.generate_text.clean_up.title', 'Clean up')}
        </SmartButton>
      </Tooltip>
      <Tooltip
        content={t(
          'l.smart_description.change_tone.tooltip.text',
          `Enter a description and we will help you change the tone, powered by ChatGPT AI.`
        )}
        openOnClick={false}
      >
        <SmartButton onClick={handleClickChangeTone} id="application-description-ai-tone">
          {t('l.smart_description.change_tone.change_tone.title', 'Change tone')}
        </SmartButton>
      </Tooltip>
    </CustomBarWrapper>
  )
}

export default ButtonBar
